import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { usePeriodStatus } from "../hooks/usePeriodStatus";
import { canWrite, isAdmin } from "../utils/auth";
import { MONTHS, YEARS, formatNum, formatInteger } from "../utils/constants";
import { cellNumericValue, commitCellExpression, parseCellInput } from "../utils/cellExpression";
import "../styles/shared.css";
import "../styles/dashboard.css";

const COST_COLORS = {
  electricity: "#3498DB",
  water: "#1ABC9C",
  lng: "#E67E22",
  chemical: "#9B59B6",
  personnel: "#27AE60",
  total: "#2C3E50",
  metric: "#6889A5",
  eur: "#8E44AD",
};

const COST_BREAKDOWN = [
  { key: "electricity_cost", label: "Elektrik" },
  { key: "water_cost", label: "Su" },
  { key: "lng_cost", label: "LNG" },
  { key: "chemical_cost", label: "Kimyasal" },
  { key: "personnel_cost", label: "Personel" },
];

const EMPTY_COST = {
  electricity_qty: "", electricity_unit_price: "", electricity_cost: "",
  water_qty: "", water_unit_price: "", water_cost: "",
  lng_qty: "", lng_unit_price: "", lng_cost: "",
  chemical_cost: "", personnel_cost: "", total_cost: "", exchange_rate: "",
};

function num(val) {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
}

export default function CostEntryScreen() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const admin = isAdmin(user);

  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7);
  const [hotels, setHotels] = useState([]);
  const [cost, setCost] = useState(EMPTY_COST);
  const [metrics, setMetrics] = useState({});
  const [yearlyData, setYearlyData] = useState(null);
  const [tab, setTab] = useState("monthly");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const { isClosed } = usePeriodStatus(year, month);
  const readOnly = !writable || (isClosed && !admin);

  useEffect(() => {
    client.get("/hotels/").then((res) => setHotels(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    client.get("/cost-records/", { params: { year, month } })
      .then((res) => {
        if (res.data.length) {
          const c = res.data[0];
          setCost({
            electricity_qty: c.electricity_qty ?? "",
            electricity_unit_price: c.electricity_unit_price ?? "",
            electricity_cost: c.electricity_cost ?? "",
            water_qty: c.water_qty ?? "",
            water_unit_price: c.water_unit_price ?? "",
            water_cost: c.water_cost ?? "",
            lng_qty: c.lng_qty ?? "",
            lng_unit_price: c.lng_unit_price ?? "",
            lng_cost: c.lng_cost ?? "",
            chemical_cost: c.chemical_cost ?? "",
            personnel_cost: c.personnel_cost ?? "",
            total_cost: c.total_cost ?? "",
            exchange_rate: c.exchange_rate ?? "",
          });
        } else {
          setCost(EMPTY_COST);
        }
      })
      .catch(() => setCost(EMPTY_COST));

    client.get("/hotel-monthly-metrics/", { params: { year, month } })
      .then((res) => {
        const next = {};
        res.data.forEach((m) => {
          next[m.hotel_id] = {
            overnight_count: m.overnight_count ?? "",
            washed_laundry_kg: m.washed_laundry_kg ?? "",
          };
        });
        setMetrics(next);
      })
      .catch(() => setMetrics({}));
  }, [year, month]);

  useEffect(() => {
    if (tab !== "yearly") return;
    client.get("/reports/yearly-cost/", { params: { year } })
      .then((res) => setYearlyData(res.data))
      .catch(() => setYearlyData(null));
  }, [year, tab]);

  const computedTotal = useMemo(() => {
    return (
      num(cost.electricity_cost) + num(cost.water_cost) + num(cost.lng_cost) +
      num(cost.chemical_cost) + num(cost.personnel_cost)
    );
  }, [cost]);

  const totals = useMemo(() => {
    let overnight = 0;
    let washed = 0;
    hotels.forEach((h) => {
      overnight += cellNumericValue(metrics[h.id]?.overnight_count, { integer: true });
      washed += cellNumericValue(metrics[h.id]?.washed_laundry_kg);
    });
    return { overnight, washed };
  }, [hotels, metrics]);

  const kgPerGuest = totals.overnight > 0 ? totals.washed / totals.overnight : null;
  const totalCostVal = num(cost.total_cost) || computedTotal;
  const costPerGuest = totals.overnight > 0 ? totalCostVal / totals.overnight : null;
  const kgCostEur = totals.washed > 0 && num(cost.exchange_rate) > 0
    ? totalCostVal / num(cost.exchange_rate) / totals.washed
    : null;

  const yearlyMonths = useMemo(
    () => yearlyData?.months?.filter((m) => m.has_data) ?? [],
    [yearlyData],
  );

  const yearlyChartData = useMemo(
    () => yearlyMonths.map((m) => ({
      name: MONTHS[m.month - 1].slice(0, 3),
      total: Number(m.total_cost) || 0,
    })),
    [yearlyMonths],
  );

  const yearlyKpis = useMemo(() => {
    const t = yearlyData?.totals;
    if (!t) return [];
    const overnight = Number(t.overnight_count) || 0;
    const washed = Number(t.washed_laundry_kg) || 0;
    const total = Number(t.total_cost) || 0;
    return [
      { key: "y-total", label: "Yıllık Toplam Maliyet", value: total, unit: "TL", accent: COST_COLORS.total, digits: 2 },
      { key: "y-overnight", label: "Toplam Geceleme", value: overnight, unit: "kişi", accent: COST_COLORS.metric, digits: 0 },
      { key: "y-washed", label: "Toplam Yıkanan", value: washed, unit: "kg", accent: COST_COLORS.water, digits: 1 },
      {
        key: "y-cost-guest",
        label: "Kişi Başı Maliyet",
        value: overnight > 0 ? total / overnight : null,
        unit: "TL",
        accent: COST_COLORS.lng,
        digits: 2,
      },
      {
        key: "y-kg-guest",
        label: "Kişi Başı kg",
        value: overnight > 0 ? washed / overnight : null,
        unit: "kg",
        accent: COST_COLORS.chemical,
        digits: 3,
      },
    ];
  }, [yearlyData]);

  const yearlyTotalBreakdown = useMemo(() => {
    const t = yearlyData?.totals;
    if (!t) return [];
    const total = Number(t.total_cost) || 0;
    return COST_BREAKDOWN.map(({ key, label }) => {
      const amount = Number(t[key]) || 0;
      const share = total > 0 ? (amount / total) * 100 : 0;
      return {
        key,
        label,
        amount,
        share,
        color: COST_COLORS[key.replace("_cost", "")] || COST_COLORS.total,
      };
    }).filter((item) => item.amount > 0);
  }, [yearlyData]);

  function getMonthBreakdown(monthRow) {
    const total = Number(monthRow.total_cost) || 0;
    return COST_BREAKDOWN.map(({ key, label }) => {
      const amount = Number(monthRow[key]) || 0;
      const share = total > 0 ? (amount / total) * 100 : 0;
      return {
        key,
        label,
        amount,
        share,
        color: COST_COLORS[key.replace("_cost", "")] || COST_COLORS.total,
      };
    }).filter((item) => item.amount > 0);
  }

  function YearlyChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
      <div className="cost-chart-tooltip">
        <div className="cost-chart-tooltip-title">{label}</div>
        <div>{formatNum(payload[0]?.value, 2)} TL</div>
      </div>
    );
  }

  function updateCost(field, value) {
    setCost((prev) => {
      const next = { ...prev, [field]: value };
      if (field.endsWith("_qty") || field.endsWith("_unit_price")) {
        const base = field.replace(/_qty$|_unit_price$/, "");
        if (["electricity", "water", "lng"].includes(base)) {
          next[`${base}_cost`] = (num(next[`${base}_qty`]) * num(next[`${base}_unit_price`])).toFixed(2);
        }
      }
      return next;
    });
  }

  function updateMetric(hotelId, field, value) {
    const integer = field === "overnight_count";
    setMetrics((prev) => ({
      ...prev,
      [hotelId]: {
        ...prev[hotelId],
        [field]: parseCellInput(value, { integer }),
      },
    }));
  }

  function commitMetric(hotelId, field) {
    const integer = field === "overnight_count";
    setMetrics((prev) => {
      const current = prev[hotelId]?.[field];
      if (current === "" || current == null || typeof current === "number") return prev;
      return {
        ...prev,
        [hotelId]: {
          ...prev[hotelId],
          [field]: commitCellExpression(current, { integer }),
        },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveMessage("");
    try {
      const existing = await client.get("/cost-records/", { params: { year, month } });
      const payload = {
        ...cost,
        total_cost: cost.total_cost || computedTotal.toFixed(2),
      };
      if (existing.data.length) {
        await client.put(`/cost-records/${existing.data[0].id}/`, payload);
      } else {
        await client.post("/cost-records/", { ...payload, year, month });
      }

      const metricRecords = hotels.map((h) => ({
        hotel_id: h.id,
        year,
        month,
        overnight_count: cellNumericValue(metrics[h.id]?.overnight_count, { integer: true }),
        washed_laundry_kg: cellNumericValue(metrics[h.id]?.washed_laundry_kg),
      }));
      await client.post("/hotel-monthly-metrics/bulk/", { records: metricRecords });
      setSaveMessage("Kaydedildi.");
    } catch {
      setSaveMessage("Kaydedilemedi, backend bağlantısını kontrol et.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Maliyet Giriş ve Raporlama</h2>
        <p>Aylık maliyetler, geceleme ve yıkanan çamaşır — otomatik hesaplamalar</p>
      </div>

      {isClosed && tab === "monthly" && <div className="locked-banner">🔒 Bu dönem kapalı.</div>}

      <div className="toolbar">
        <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
        </select>
        <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="tab-group">
          <button className={`ui-btn ${tab === "monthly" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("monthly")}>Aylık Giriş</button>
          <button className={`ui-btn ${tab === "yearly" ? "active" : "ui-btn--ghost"}`} onClick={() => setTab("yearly")}>Yıllık Özet</button>
        </div>
        {tab === "monthly" && !readOnly && (
          <button className="ui-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        )}
        {saveMessage && <span className="status-msg success">{saveMessage}</span>}
      </div>

      {tab === "monthly" ? (
        <>
          <div className="panel">
            <h2>Aylık Maliyetler</h2>
            <div className="form-grid">
              {[
                ["electricity_qty", "Elektrik miktar"], ["electricity_unit_price", "Elektrik birim fiyat"],
                ["electricity_cost", "Elektrik maliyet"], ["water_qty", "Su miktar"],
                ["water_unit_price", "Su birim fiyat"], ["water_cost", "Su maliyet"],
                ["lng_qty", "LNG miktar"], ["lng_unit_price", "LNG birim fiyat"],
                ["lng_cost", "LNG maliyet"], ["chemical_cost", "Kimyasal maliyet"],
                ["personnel_cost", "Personel maliyet"], ["total_cost", "Toplam maliyet (TL)"],
                ["exchange_rate", "Kur (€)"],
              ].map(([field, label]) => (
                <div className="form-field" key={field}>
                  <label>{label}</label>
                  <input type="number" min="0" step="0.01" disabled={readOnly}
                    value={cost[field]}
                    onChange={(e) => updateCost(field, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="calc-row">
              <span>Hesaplanan toplam: {formatNum(computedTotal, 2)} TL</span>
              {Math.abs(computedTotal - num(cost.total_cost)) > 0.01 && num(cost.total_cost) > 0 && (
                <span className="warning">Girilen toplam ile hesaplanan toplam farklı.</span>
              )}
            </div>
          </div>

          <div className="panel">
            <h2>Otel Bazlı Geceleme ve Yıkanan Çamaşır</h2>
            <table className="data-grid">
              <thead>
                <tr>
                  <th className="label-col">Otel</th>
                  <th>Geceleme</th>
                  <th>Yıkanan çamaşır (kg)</th>
                </tr>
              </thead>
              <tbody>
                {hotels.map((h) => (
                  <tr key={h.id}>
                    <td className="label-col">{h.name}</td>
                    <td>
                      <input type="text" inputMode="numeric" disabled={readOnly}
                        value={metrics[h.id]?.overnight_count ?? ""}
                        onChange={(e) => updateMetric(h.id, "overnight_count", e.target.value)}
                        onBlur={() => commitMetric(h.id, "overnight_count")}
                      />
                    </td>
                    <td>
                      <input type="text" inputMode="decimal" disabled={readOnly}
                        value={metrics[h.id]?.washed_laundry_kg ?? ""}
                        onChange={(e) => updateMetric(h.id, "washed_laundry_kg", e.target.value)}
                        onBlur={() => commitMetric(h.id, "washed_laundry_kg")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="summary-cards">
            <div className="summary-card"><h3>Kişi başı kg</h3><p>{kgPerGuest != null ? formatNum(kgPerGuest, 3) : "-"}</p></div>
            <div className="summary-card"><h3>Kişi başı maliyet (TL)</h3><p>{costPerGuest != null ? formatNum(costPerGuest, 2) : "-"}</p></div>
            <div className="summary-card"><h3>kg maliyet (€)</h3><p>{kgCostEur != null ? formatNum(kgCostEur, 4) : "-"}</p></div>
          </div>
          {totals.overnight === 0 && (
            <p className="warning">Geceleme sıfır — kişi başı hesaplamalar yapılamaz.</p>
          )}
        </>
      ) : (
        <div className="cost-yearly-wrap">
          {!yearlyData && <div className="dash-empty">Yükleniyor...</div>}

          {yearlyData && yearlyMonths.length === 0 && (
            <div className="dash-empty">{year} yılı için henüz maliyet verisi yok.</div>
          )}

          {yearlyData && yearlyMonths.length > 0 && (
            <>
              <div className="kpi-grid cost-kpi-grid">
                {yearlyKpis.map((kpi) => (
                  <div key={kpi.key} className="kpi-card" style={{ "--accent": kpi.accent }}>
                    <span className="kpi-label">{kpi.label}</span>
                    <span className="kpi-value">
                      {kpi.value != null && !Number.isNaN(kpi.value) ? formatNum(kpi.value, kpi.digits) : "-"}
                      <small>{kpi.unit}</small>
                    </span>
                  </div>
                ))}
              </div>

              {yearlyChartData.length > 1 && (
                <div className="dash-card cost-yearly-chart">
                  <h3>Aylık Toplam Maliyet Trendi</h3>
                  <div className="dash-chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={yearlyChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#7F8C8D" }} axisLine={{ stroke: "#CAD6DF" }} tickLine={false} />
                        <YAxis
                          tick={{ fontSize: 11, fill: "#7F8C8D" }}
                          axisLine={false}
                          tickLine={false}
                          width={72}
                          tickFormatter={(v) => formatInteger(v)}
                        />
                        <Tooltip content={<YearlyChartTooltip />} cursor={{ fill: "rgba(52,152,219,0.08)" }} />
                        <Bar dataKey="total" name="Toplam" fill={COST_COLORS.total} radius={[6, 6, 0, 0]} maxBarSize={42} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="cost-month-grid">
                {yearlyMonths.map((m) => {
                  const breakdown = getMonthBreakdown(m);
                  const monthTotal = Number(m.total_cost) || 0;
                  const costPerGuestMonth = m.overnight_count > 0 ? monthTotal / m.overnight_count : null;
                  return (
                    <article key={m.month} className="cost-month-card">
                      <header className="cost-month-card-head">
                        <div>
                          <span className="cost-month-index">{String(m.month).padStart(2, "0")}</span>
                          <h4>{MONTHS[m.month - 1]}</h4>
                        </div>
                        <div className="cost-month-total">
                          <span>Toplam</span>
                          <strong>{formatNum(monthTotal, 2)}<small>TL</small></strong>
                        </div>
                      </header>

                      <div className="cost-month-meta">
                        <div className="cost-month-meta-item">
                          <span>Geceleme</span>
                          <strong>{formatInteger(m.overnight_count || 0)}</strong>
                        </div>
                        <div className="cost-month-meta-item">
                          <span>Yıkanan</span>
                          <strong>{formatNum(m.washed_laundry_kg, 1)}<small>kg</small></strong>
                        </div>
                        <div className="cost-month-meta-item">
                          <span>Kişi başı</span>
                          <strong>{costPerGuestMonth != null ? formatNum(costPerGuestMonth, 2) : "-"}<small>TL</small></strong>
                        </div>
                      </div>

                      {breakdown.length > 0 ? (
                        <div className="cost-month-breakdown">
                          {breakdown.map((item) => (
                            <div key={item.key} className="cost-month-breakdown-row">
                              <div className="cost-month-breakdown-label">
                                <span>{item.label}</span>
                                <span>{formatNum(item.amount, 2)} TL</span>
                              </div>
                              <div className="cost-breakdown-track">
                                <div
                                  className="cost-breakdown-fill"
                                  style={{ width: `${Math.min(item.share, 100)}%`, background: item.color }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="cost-month-empty">Bu ay yalnızca geceleme / yıkanan verisi var.</p>
                      )}
                    </article>
                  );
                })}
              </div>

              {yearlyData.totals && (
                <div className="dash-card cost-year-total-card">
                  <div className="cost-summary-head">
                    <div>
                      <h3>{year} Yıllık Toplam</h3>
                      <p>Tüm ayların birleşik maliyet dağılımı</p>
                    </div>
                    <div className="cost-summary-total">
                      <span>Genel Toplam</span>
                      <strong>
                        {formatNum(yearlyData.totals.total_cost, 2)}
                        <small>TL</small>
                      </strong>
                    </div>
                  </div>

                  <div className="cost-year-total-stats">
                    <div className="cost-month-meta-item">
                      <span>Geceleme</span>
                      <strong>{formatInteger(yearlyData.totals.overnight_count || 0)}</strong>
                    </div>
                    <div className="cost-month-meta-item">
                      <span>Yıkanan</span>
                      <strong>{formatNum(yearlyData.totals.washed_laundry_kg, 1)}<small>kg</small></strong>
                    </div>
                  </div>

                  <div className="cost-breakdown">
                    {yearlyTotalBreakdown.map((item) => (
                      <div key={item.key} className="cost-breakdown-row">
                        <div className="cost-breakdown-meta">
                          <span className="cost-breakdown-label">{item.label}</span>
                          <span className="cost-breakdown-value">
                            {formatNum(item.amount, 2)} TL · %{formatNum(item.share, 1)}
                          </span>
                        </div>
                        <div className="cost-breakdown-track">
                          <div
                            className="cost-breakdown-fill"
                            style={{ width: `${Math.min(item.share, 100)}%`, background: item.color }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
